import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { mediaUrl } from "lib/media";

export const videosPrimary: IDemoReelVideo = {
  "planetary explosion": {
    main: mediaUrl("website_files/videos/planet/PlanetBreak2.mov"),
    breakdown: mediaUrl(
      "website_files/videos/planet/PlanetBreakdown2.mov",
    ),
    isIFrame: false,
    current: "main",
    title: "Planetary Explosion",
    description:
      "Using Octane for the ice like shaders was very important to keep render times low here. Most volumes were also kept within houdini but all godrays were done using nuke",
  },
  volcano: {
    main: "https://player.vimeo.com/video/606038190?h=48f1b3be27",
    breakdown: "https://player.vimeo.com/video/606074390?h=06c79e3938",
    isIFrame: true,
    current: "main",
    title: "Underwater Volcano",
    description:
      "Used a volcano in Hawaii as inspiration for this. Had to do some extra work in nuke to really make a vibrant afternoon scene.",
  },

  disintegrate: {
    isIFrame: false,
    main: mediaUrl("website_files/videos/sword2/Disintegrate2.mov"),
    breakdown: mediaUrl(
      "website_files/videos/sword2/DisintegrateBreakdown2.mov",
    ),
    current: "main",
    title: "Metal Disintegration ",
    description:
      "My final iteration of the disintegration effect. Really pushing for better lighting and more complex photon maps.",
  },
  ocean: {
    breakdown: mediaUrl("website_files/videos/ship/shipbreakdown.mov"),
    main: mediaUrl("website_files/videos/ship/ship2.mov"),
    current: "main",
    title: "Ocean",
    description:
      "The scale of this shot extended render times but it was still done in reasonable time. I’m responsible for all aspects except modeling the ship. Everything was done in Houdini and composited in Nuke. ",
    isIFrame: false,
  },
  tornado: {
    breakdown: mediaUrl("website_files/videos/tornado/tornado.mov"),
    main: mediaUrl("website_files/videos/tornado/tornado_breakdown.mov"),
    current: "main",
    title: "Tornado",
    description:
      "This tornado was made by projecting UV’s on an animated cylinder. While it looks very dense, the simulation times are very quick. Background was filmed by me. All effects were done in Houdini and composited in Nuke.",
    isIFrame: false,
  },
  underwater: {
    breakdown: mediaUrl(
      "website_files/videos/underwater/underwater_breakdown.mov",
    ),
    main: mediaUrl("website_files/videos/underwater/underwater.mov"),
    current: "main",
    title: "Underwater",
    description:
      "This shot was made using a lot of noise with edge detection and mixing color channels in a 3d scene. Most of this shot was done using Nuke with some additional effects from Houdini.",
    isIFrame: false,
  },
};

interface IDemoReelVideo {
  [key: string]: {
    main: string;
    breakdown: string | null;
    isIFrame: boolean;
    current: string;
    title: string;
    description: string;
  };
}

export const videosOtherWorks: IDemoReelVideo = {
  lava: {
    breakdown: mediaUrl("website_files/videos/lava/lavabreakdown.mov"),
    main: mediaUrl("website_files/videos/lava/lava.mov"),
    current: "main",
    title: "Falling Lava",
    description: "This shot was made based off the tutorial by Ben Watts.",
    isIFrame: false,
  },
  sword: {
    breakdown: mediaUrl("website_files/videos/sword/sword_breakdown.mov"),
    main: mediaUrl("website_files/videos/sword/sword.mov"),
    current: "main",
    title: "Sword",
    description:
      "This was done by making changes to an existing tool I had. In order to allow me to use materials on the objects, I had to delete the faces as opposed making their opacity 0 like I did on the other scenes. This was done in Houdini and composited in Nuke.",
    isIFrame: false,
  },
  treegrowth: {
    breakdown: mediaUrl("website_files/videos/treegrowth/breakdown.mov"),
    main: mediaUrl("website_files/videos/treegrowth/beauty_2.mov"),
    current: "main",
    title: "Tree Growth",
    description:
      "This tree growth was made off a tool using L-systems. It can also support the look of actual trees and even vines. It used packed objects to allow me to potentially replicate very dense geometry with barely any extra strain on the computer. This was done in Houdini and composited in Nuke.",
    isIFrame: false,
  },
  cloud: {
    breakdown: null,
    main: mediaUrl("website_files/videos/clouds/cloudship.mov"),
    current: "main",
    title: "Cloudship",
    description:
      "This shot was made based off the tutorial by Andreas Vrhovsek.",
    isIFrame: false,
  },
  gears: {
    breakdown: null,
    main: mediaUrl("website_files/videos/transmog/gears.mov"),
    current: "main",
    title: "Gears",
    description: "This shot was made based off the tutorial by Jeff Wolverton.",
    isIFrame: false,
  },
  ship: {
    breakdown: null,
    main: mediaUrl("website_files/videos/transmog/ship.mov"),
    current: "main",
    title: "Ship",
    description: "This shot was made based off the tutorial by Jeff Wolverton.",
    isIFrame: false,
  },
  candle: {
    breakdown: mediaUrl("website_files/videos/candle/candle_breakdown.mov"),
    main: mediaUrl("website_files/videos/candle/candle.mov"),
    current: "main",
    title: "Candle",
    description:
      "I put a lot of work into the shaders of the smoke on this shot. The debris had a bit of a ripple effect but some compositing and the smoke fixed it naturally. This was modeled in Maya, and effects were done in Houdini. Composited in Nuke.",
    isIFrame: false,
  },
};

export const DemoReel = ({ videos }: { videos: IDemoReelVideo }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const videoName = searchParams.get("video");
  const isPrimaryCollection = videos === videosPrimary;
  const videoRefs = useRef<Record<string, HTMLElement | null>>({});

  useEffect(() => {
    if (videoName) {
      videoRefs.current[videoName]?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("video");
      setSearchParams(nextParams, { replace: true });
    }
    // This should run only when the page is opened with a target project.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoName]);

  const [imgBreakdownStatus, setImgBreakdownStatus] = useState(
    Object.keys(videos).reduce<Record<string, boolean>>((acc, key) => {
      acc[key] = false;
      return acc;
    }, {}),
  );

  return (
    <div className="bg-background text-foreground">
      <section className="border-b border-white/10">
        <div className="page-shell py-20 sm:py-28 lg:py-32">
          <div className="grid gap-10 lg:grid-cols-12 lg:items-end lg:gap-8">
            <div className="lg:col-span-8">
              <p className="eyebrow reveal-up">
                {isPrimaryCollection
                  ? "Reel + breakdowns"
                  : "R&D + experiments"}
              </p>
              <h1 className="page-title reveal-up reveal-delay-1 mt-7">
                {isPrimaryCollection ? "Selected work." : "The archive."}
              </h1>
            </div>
            <p className="reveal-up reveal-delay-2 max-w-xl text-sm leading-7 text-white/50 lg:col-span-4 lg:pb-2">
              {isPrimaryCollection
                ? "A selection of simulation, procedural FX, lighting, and compositing work. Switch to each breakdown to see how the final frame came together."
                : "Tests, studies, and smaller explorations—an ongoing record of ideas developed through Houdini, Maya, and Nuke."}
            </p>
          </div>
        </div>
      </section>

      <section className="page-shell pb-24 sm:pb-32">
        {Object.keys(videos).map((key, index) => {
          const video = videos[key];
          const isBreakdown = imgBreakdownStatus[key];
          const videoUrl =
            isBreakdown && video.breakdown !== null
              ? video.breakdown
              : video.main;
          const projectNumber = String(index + 1).padStart(2, "0");

          return (
            <article
              className="scroll-mt-28 border-b border-white/10 py-16 last:border-b-0 sm:py-24"
              ref={(element) => {
                videoRefs.current[key] = element;
              }}
              key={key}
            >
              <div className="grid gap-10 lg:grid-cols-12 lg:gap-10">
                <div className="lg:col-span-8">
                  <div className="relative aspect-video overflow-hidden border border-white/10 bg-black shadow-2xl shadow-black/30">
                    {video.isIFrame ? (
                      <iframe
                        key={`${key}-${isBreakdown ? "breakdown" : "main"}`}
                        src={videoUrl}
                        title={`${video.title} ${
                          isBreakdown ? "breakdown" : "final"
                        }`}
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                        className="h-full w-full"
                      />
                    ) : (
                      <video
                        key={`${key}-${isBreakdown ? "breakdown" : "main"}`}
                        src={videoUrl}
                        controls
                        playsInline
                        preload="metadata"
                        className="h-full w-full object-contain"
                      >
                        Your browser does not support embedded video.
                      </video>
                    )}
                  </div>
                </div>

                <div className="flex flex-col lg:col-span-4 lg:py-2">
                  <p className="text-xs font-semibold text-ember">
                    PROJECT / {projectNumber}
                  </p>
                  <h2 className="mt-6 text-3xl font-semibold tracking-[-0.035em] sm:text-4xl">
                    {video.title}
                  </h2>
                  <p className="mt-6 text-sm leading-7 text-white/50">
                    {video.description}
                  </p>

                  {video.breakdown !== null && (
                    <div className="mt-9 inline-flex self-start border border-white/10 bg-white/[0.025] p-1">
                      <button
                        type="button"
                        aria-pressed={!isBreakdown}
                        className={`project-toggle ${
                          !isBreakdown ? "project-toggle-active" : ""
                        }`}
                        onClick={() =>
                          setImgBreakdownStatus((current) => ({
                            ...current,
                            [key]: false,
                          }))
                        }
                      >
                        Final
                      </button>
                      <button
                        type="button"
                        aria-pressed={isBreakdown}
                        className={`project-toggle ${
                          isBreakdown ? "project-toggle-active" : ""
                        }`}
                        onClick={() =>
                          setImgBreakdownStatus((current) => ({
                            ...current,
                            [key]: true,
                          }))
                        }
                      >
                        Breakdown
                      </button>
                    </div>
                  )}

                  <div className="mt-auto hidden border-t border-white/10 pt-5 lg:block">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/30">
                      FX · Lighting · Compositing
                    </p>
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </section>
    </div>
  );
};
